import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Skeleton from '../ui/Skeleton';
import './ProductStepPendings.css';

const INITIAL_STATE = { status: 'loading', items: [], error: null };

function formatDate(value) {
  if (!value) return 'Data indisponível';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Data indisponível';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function ProductStepPendings({ pecaId }) {
  const [state, setState] = useState(INITIAL_STATE);
  const [retryingIds, setRetryingIds] = useState(() => new Set());
  const [itemMessages, setItemMessages] = useState({});
  const [notice, setNotice] = useState(null);
  const [discardPending, setDiscardPending] = useState(null);
  const [discardReason, setDiscardReason] = useState('');
  const [discardError, setDiscardError] = useState(null);
  const [discardingId, setDiscardingId] = useState(null);
  const requestSequence = useRef(0);
  const retriesInFlight = useRef(new Set());
  const discardsInFlight = useRef(new Set());
  const fileInputs = useRef(new Map());
  const mounted = useRef(true);

  useEffect(() => {
    const activeRetries = retriesInFlight.current;
    const activeDiscards = discardsInFlight.current;
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestSequence.current += 1;
      activeRetries.clear();
      activeDiscards.clear();
    };
  }, []);

  const loadPendings = useCallback(async ({ showLoading = true } = {}) => {
    const sequence = ++requestSequence.current;
    if (showLoading) setState(INITIAL_STATE);

    try {
      const response = await api.get(`/pecas/${pecaId}/step/pendencias`);
      if (sequence !== requestSequence.current || !mounted.current) return;
      if (!Array.isArray(response.data)) {
        throw new Error('Resposta inválida ao listar pendências STEP.');
      }
      setState({ status: 'success', items: response.data, error: null });
    } catch (error) {
      if (sequence !== requestSequence.current || !mounted.current) return;
      console.error('ProductStepPendings: falha ao carregar pendências STEP:', error);
      setState({
        status: 'error',
        items: [],
        error: 'Não foi possível carregar as pendências STEP desta peça.',
      });
    }
  }, [pecaId]);

  useEffect(() => {
    loadPendings();
    return () => {
      requestSequence.current += 1;
    };
  }, [loadPendings]);

  function setRetrying(pendingId, retrying) {
    setRetryingIds((current) => {
      const next = new Set(current);
      if (retrying) next.add(pendingId);
      else next.delete(pendingId);
      return next;
    });
  }

  async function retryPending(pending, file) {
    if (!file || retriesInFlight.current.has(pending.id)) return;

    retriesInFlight.current.add(pending.id);
    setRetrying(pending.id, true);
    setNotice(null);
    setItemMessages((current) => {
      const next = { ...current };
      delete next[pending.id];
      return next;
    });

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(
        `/pecas/${pecaId}/step/pendencias/${pending.id}/confirmar`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (!mounted.current) return;
      setState((current) => ({
        ...current,
        items: current.items.filter((item) => item.id !== pending.id),
      }));
      setNotice({
        type: 'success',
        message: 'Pendência STEP confirmada. A geometria salva não foi alterada.',
      });
    } catch (error) {
      if (!mounted.current) return;
      const payload = error.response?.data;

      if (payload?.code === 'STEP_FILE_HASH_MISMATCH') {
        setItemMessages((current) => ({
          ...current,
          [pending.id]: {
            type: 'error',
            message: 'O arquivo selecionado não corresponde ao STEP esperado. Selecione o arquivo original e tente novamente.',
          },
        }));
      } else if (payload?.code === 'STEP_IDENTITY_CONFLICT') {
        setState((current) => ({
          ...current,
          items: current.items.map((item) => (
            item.id === pending.id ? { ...item, estado: 'CONFLICT' } : item
          )),
        }));
        setItemMessages((current) => ({
          ...current,
          [pending.id]: {
            type: 'warning',
            message: 'A identidade STEP está associada a outra peça. Esse conflito exigirá tratamento específico futuro.',
          },
        }));
      } else if (error.response?.status === 404) {
        await loadPendings({ showLoading: false });
        if (!mounted.current) return;
        setNotice({
          type: 'neutral',
          message: 'A pendência não está mais disponível. A lista foi atualizada.',
        });
      } else {
        console.error('ProductStepPendings: falha ao retomar pendência STEP:', error);
        setItemMessages((current) => ({
          ...current,
          [pending.id]: {
            type: 'error',
            message: 'Não foi possível confirmar a pendência STEP. Tente novamente.',
          },
        }));
      }
    } finally {
      retriesInFlight.current.delete(pending.id);
      if (mounted.current) setRetrying(pending.id, false);
    }
  }

  function handleFileChange(pending, event) {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (file) retryPending(pending, file);
  }

  function openDiscardDialog(pending) {
    if (pending.estado !== 'CONFLICT') return;
    setDiscardPending(pending);
    setDiscardReason('');
    setDiscardError(null);
  }

  function closeDiscardDialog() {
    if (discardingId) return;
    setDiscardPending(null);
    setDiscardReason('');
    setDiscardError(null);
  }

  async function discardConflict() {
    if (!discardPending || discardsInFlight.current.has(discardPending.id)) return;

    const normalizedReason = discardReason.trim();
    if (normalizedReason.length < 10 || normalizedReason.length > 500) {
      setDiscardError('Informe um motivo entre 10 e 500 caracteres.');
      return;
    }

    const pendingId = discardPending.id;
    discardsInFlight.current.add(pendingId);
    setDiscardingId(pendingId);
    setDiscardError(null);
    setNotice(null);

    try {
      const response = await api.post(
        `/pecas/${pecaId}/step/pendencias/${pendingId}/descartar`,
        {
          confirmar_descarte: true,
          motivo: normalizedReason,
        }
      );
      if (!mounted.current) return;
      if (response.data?.status !== 'DISCARDED') {
        throw new Error('Resposta inválida ao descartar pendência STEP.');
      }

      setState((current) => ({
        ...current,
        items: current.items.filter((item) => item.id !== pendingId),
      }));
      setDiscardPending(null);
      setDiscardReason('');
      setDiscardError(null);
      setNotice({
        type: 'success',
        message: 'Conflito descartado. A proveniência confirmada, a peça e a geometria foram preservadas.',
      });
    } catch (error) {
      if (!mounted.current) return;
      const status = error.response?.status;

      if (status === 404) {
        await loadPendings({ showLoading: false });
        if (!mounted.current) return;
        setDiscardPending(null);
        setDiscardReason('');
        setDiscardError(null);
        setNotice({
          type: 'neutral',
          message: 'A pendência não está mais disponível. A lista foi atualizada.',
        });
      } else if (status === 409) {
        await loadPendings({ showLoading: false });
        if (!mounted.current) return;
        setDiscardPending(null);
        setDiscardReason('');
        setDiscardError(null);
        setNotice({
          type: 'warning',
          message: 'O estado da pendência mudou. Revise a lista atualizada antes de decidir novamente.',
        });
      } else if (
        status === 500
        && error.response?.data?.code === 'STEP_PENDING_DISCARD_AUDIT_FAILED'
      ) {
        setDiscardError('O descarte não foi concluído porque a auditoria obrigatória falhou. Tente novamente mais tarde.');
      } else {
        console.error('ProductStepPendings: falha ao descartar conflito STEP:', error);
        setDiscardError('Não foi possível descartar o conflito. Tente novamente.');
      }
    } finally {
      discardsInFlight.current.delete(pendingId);
      if (mounted.current) setDiscardingId(null);
    }
  }

  if (state.status === 'success' && state.items.length === 0 && !notice) {
    return null;
  }

  return (
    <section className="cw-step-pendings" aria-labelledby="cw-step-pendings-title">
      <div className="cw-step-pendings-header">
        <h3 id="cw-step-pendings-title" className="cw-product-details-section-title">
          Pendências STEP
        </h3>
        {state.status === 'success' && state.items.length > 0 && (
          <Badge variant="neutral">{state.items.length}</Badge>
        )}
      </div>

      {state.status === 'loading' && (
        <div className="cw-step-pendings-loading" role="status" aria-label="Carregando pendências STEP">
          <Skeleton height="12px" />
          <Skeleton height="28px" />
        </div>
      )}

      {state.status === 'error' && (
        <p className="cw-step-pendings-alert cw-step-pendings-alert--error" role="alert">
          {state.error}
        </p>
      )}

      {notice && (
        <p
          className={`cw-step-pendings-alert cw-step-pendings-alert--${notice.type}`}
          role="status"
        >
          {notice.message}
        </p>
      )}

      {state.status === 'success' && state.items.length > 0 && (
        <ul className="cw-step-pendings-list">
          {state.items.map((pending) => {
            const isConflict = pending.estado === 'CONFLICT';
            const isRetrying = retryingIds.has(pending.id);
            const message = itemMessages[pending.id];

            return (
              <li className="cw-step-pending-item" key={pending.id}>
                <div className="cw-step-pending-summary">
                  <Badge variant={isConflict ? 'danger' : 'warning'} dot>
                    {isConflict ? 'CONFLICT' : 'PENDING'}
                  </Badge>
                  <span className="cw-step-pending-date">
                    Geometria salva em {formatDate(pending.geometry_saved_at)}
                  </span>
                </div>
                <p className="cw-step-pending-identity">
                  Ordinal bruto: {pending.raw_solid_ordinal} · Identificador v{pending.identifier_version}
                </p>

                {isConflict ? (
                  <>
                    <p className="cw-step-pending-guidance">
                      Conflito de proveniência. A origem confirmada permanece associada à outra peça.
                    </p>
                    <div className="cw-step-pending-actions">
                      <Button
                        variant="danger"
                        onClick={() => openDiscardDialog(pending)}
                      >
                        Descartar conflito
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="cw-step-pending-actions">
                    <input
                      ref={(element) => {
                        if (element) fileInputs.current.set(pending.id, element);
                        else fileInputs.current.delete(pending.id);
                      }}
                      className="cw-step-pending-file-input"
                      type="file"
                      accept=".step,.stp,.STEP,.STP"
                      aria-label={`Selecionar arquivo da pendência ${pending.id}`}
                      disabled={isRetrying}
                      onChange={(event) => handleFileChange(pending, event)}
                    />
                    <Button
                      variant="secondary"
                      disabled={isRetrying}
                      onClick={() => fileInputs.current.get(pending.id)?.click()}
                    >
                      {isRetrying ? 'Confirmando…' : 'Reapresentar arquivo STEP'}
                    </Button>
                  </div>
                )}

                {message && (
                  <p
                    className={`cw-step-pendings-alert cw-step-pendings-alert--${message.type}`}
                    role={message.type === 'error' ? 'alert' : 'status'}
                  >
                    {message.message}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {discardPending && (
        <Modal
          className="cw-step-discard-modal"
          onClose={closeDiscardDialog}
        >
          <form
            className="cw-step-discard-form"
            onSubmit={(event) => {
              event.preventDefault();
              discardConflict();
            }}
          >
            <div className="cw-step-discard-heading">
              <Badge variant="danger">CONFLICT</Badge>
              <h2>Descartar conflito STEP</h2>
            </div>

            <div className="cw-step-discard-warning">
              <strong>Esta ação é irreversível no CoreWood.</strong>
              <p>Somente o registro desta pendência será removido.</p>
              <ul>
                <li>A proveniência atualmente confirmada será preservada.</li>
                <li>A geometria será preservada.</li>
                <li>A peça será preservada.</li>
                <li>Nenhuma origem será transferida ou sobrescrita.</li>
              </ul>
            </div>

            <label className="cw-step-discard-field">
              <span>Motivo do descarte</span>
              <textarea
                className="cw-input cw-step-discard-textarea"
                value={discardReason}
                disabled={discardingId === discardPending.id}
                aria-describedby="cw-step-discard-help"
                placeholder="Descreva por que esta pendência pode ser descartada"
                onChange={(event) => {
                  setDiscardReason(event.target.value);
                  setDiscardError(null);
                }}
              />
              <small id="cw-step-discard-help">
                Entre 10 e 500 caracteres · {discardReason.length}/500
              </small>
            </label>

            {discardError && (
              <p className="cw-step-pendings-alert cw-step-pendings-alert--error" role="alert">
                {discardError}
              </p>
            )}

            <div className="cw-step-discard-actions">
              <Button type="button" variant="secondary" disabled={Boolean(discardingId)} onClick={closeDiscardDialog}>
                Cancelar
              </Button>
              <Button type="submit" variant="danger" loading={discardingId === discardPending.id}>
                Confirmar descarte
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </section>
  );
}

export default ProductStepPendings;
